/**
 * Catálogo de colunas do Cadastro de Suprimentos.
 *
 * O usuário escolhe QUAIS colunas vê e em QUE ORDEM (ColunasPanel); a
 * escolha é dele e vale em qualquer dispositivo (preferência de tabela no
 * backend). Toda célula corta com "…" e mostra o valor inteiro no title —
 * zero scroll lateral é regra constitucional do PharuERP.
 *
 * Redesign 2026-09-15: entram as colunas compostas do handoff — `item`
 * (nome + selos + código · especificação), `tipo` com
 * família, `saldo` com barra saldo × mínimo e `ultimo_preco` (fornecedor e
 * data). Nenhuma coluna antiga saiu: quem prefere "Código" e "Nome" soltos
 * continua podendo escolhê-los no painel Colunas. A amostra (textura ou cor)
 * não é coluna escolhível: a tabela a fixa no início de toda linha.
 *
 * Medidas em mm são INTEIRAS na tela (1850, não 1850,0): o backend guarda
 * Decimal(…,1) e o DRF manda "1850.0"; `formatarQuantidade` tira o zero.
 * Dinheiro sai sempre com R$ (`formatarReaisBR`).
 */
import React from "react";
import { FiAlertTriangle } from "react-icons/fi";

import { formatarDataLocal, formatarQuantidade, formatarReaisBR } from "../../../utils/dinheiro";
import { CATEGORIAS, fmtQtd } from "../helpers";
import { Pill, SubText } from "../styles";
import { especificacaoDe, infoSaldo, semFornecedor, semPreco, ultimoPrecoDe, valorEstoque } from "./derivados";
import { BarraSaldo, CelulaItem, CelulaTipo, CelulaUltimoPreco, DotSituacao } from "./celulas";

/** "1850.0" -> "1850"; "0.45" -> "0,45"; vazio -> "—". */
export function fmtMm(v) {
  if (v === null || v === undefined || v === "") return "—";
  return formatarQuantidade(v);
}

export function labelCategoria(cat) {
  return CATEGORIAS.find((c) => c.value === cat)?.label || cat || "—";
}

export function labelTipo(p, tipos) {
  if (!p.tipo_suprimento) return "—";
  return tipos.find((t) => t.codigo === p.tipo_suprimento)?.label || p.tipo_suprimento;
}

export function familiaTipo(p, tipos) {
  if (!p.tipo_suprimento) return "";
  return tipos.find((t) => t.codigo === p.tipo_suprimento)?.familia || "";
}

function veio(p) {
  if (p.tem_veio === true) return "Sim";
  if (p.tem_veio === false) return "Não";
  return "—";
}

const cor = (p) => (p.atributos || {}).cor || "";

function textoUltimoPreco(p) {
  const u = ultimoPrecoDe(p);
  if (!u) return "—";
  return `${formatarReaisBR(u.valor)}${u.fornecedor ? ` · ${u.fornecedor}` : ""}${u.data ? ` · ${formatarDataLocal(u.data)}` : ""}`;
}

/**
 * `largura` é peso relativo (a tabela normaliza para 100%). `texto(p, ctx)`
 * é o valor plano (usado no title e na exportação); `render` só quando a
 * célula tem algo além de texto. `alturaDupla` marca colunas de duas linhas.
 */
export const CATALOGO_COLUNAS = [
  {
    key: "tipo", label: "Tipo", largura: 11, texto: (p, ctx) => labelTipo(p, ctx.tipos),
    render: (p, ctx) => <CelulaTipo nome={labelTipo(p, ctx.tipos)} familia={familiaTipo(p, ctx.tipos)} />,
  },
  {
    key: "item", label: "Item", largura: 30, texto: (p, ctx) => `${p.nome || ""} · ${p.codigo || ""} · ${especificacaoDe(p, ctx.tipos)}`,
    render: (p, ctx) => (
      <CelulaItem produto={p} especificacao={especificacaoDe(p, ctx.tipos)} semFornecedor={semFornecedor(p)} />
    ),
  },
  { key: "codigo", label: "Código", largura: 12, texto: (p) => p.codigo || "" },
  {
    key: "nome", label: "Nome", largura: 30, texto: (p) => p.nome || "",
    render: (p) => (
      <>
        {p.nome}
        {!p.ativo && <SubText style={{ color: "var(--ink-4)" }}>(inativo)</SubText>}
        {/* ADR-0068: preco e opcional para sair da fila; o selo diz o que falta. */}
        {semPreco(p) && (
          <Pill $tone="warning" style={{ fontSize: "0.66rem", marginLeft: 6 }} title="Cadastrado sem custo médio: entra no orçamento com custo zero até alguém informar o preço.">sem preço</Pill>
        )}
      </>
    ),
  },
  {
    key: "categoria", label: "Categoria", largura: 12, texto: (p) => labelCategoria(p.categoria),
    render: (p) => <Pill $tone="neutral" style={{ fontSize: "0.68rem" }}>{labelCategoria(p.categoria)}</Pill>,
  },
  { key: "unidade", label: "Un", largura: 5, texto: (p) => p.unidade || "" },
  { key: "consumo_30d", label: "Consumo 30 d", largura: 9, alinhar: "right", texto: (p) => fmtQtd(p.consumo_30d) },
  { key: "consumo_90d", label: "Consumo 90 d", largura: 9, alinhar: "right", texto: (p) => fmtQtd(p.consumo_90d) },
  { key: "ultima_saida", label: "Última saída", largura: 9, texto: (p) => (p.ultima_saida_em ? formatarDataLocal(p.ultima_saida_em) : "—") },
  { key: "fornecedores", label: "Fornecedores", largura: 7, alinhar: "right", texto: (p) => String(p.fornecedores_ativos ?? 0) },
  { key: "cor", label: "Cor", largura: 12, texto: (p) => cor(p) || "—" },
  { key: "fabricante", label: "Fabricante", largura: 12, texto: (p) => p.fabricante || "—" },
  { key: "referencia_fabricante", label: "Ref. fabricante", largura: 10, texto: (p) => p.referencia_fabricante || "—" },
  { key: "ncm", label: "NCM", largura: 9, texto: (p) => p.ncm || "—" },
  { key: "espessura", label: "Esp. (mm)", largura: 8, alinhar: "right", texto: (p) => fmtMm(p.espessura_mm) },
  { key: "largura", label: "Larg. (mm)", largura: 8, alinhar: "right", texto: (p) => fmtMm(p.largura_mm) },
  { key: "comprimento", label: "Compr. (mm)", largura: 9, alinhar: "right", texto: (p) => fmtMm(p.comprimento_mm) },
  { key: "veio", label: "Veio", largura: 6, texto: (p) => veio(p) },
  { key: "refilo", label: "Refilo (mm)", largura: 8, alinhar: "right", texto: (p) => fmtMm(p.refilo_mm) },
  {
    key: "saldo", label: "Saldo · mínimo", largura: 13,
    texto: (p) => `${fmtQtd(p.saldo)} · mín ${fmtQtd(p.estoque_minimo)}`,
    render: (p) => <BarraSaldo info={infoSaldo(p)} saldoNegativo={Number(p.saldo) < 0} />,
  },
  { key: "reservado", label: "Reservado", largura: 8, alinhar: "right", texto: (p) => fmtQtd(p.reservado) },
  { key: "disponivel", label: "Disponível", largura: 8, alinhar: "right", texto: (p) => fmtQtd(p.disponivel) },
  { key: "estoque_minimo", label: "Mínimo", largura: 8, alinhar: "right", texto: (p) => fmtQtd(p.estoque_minimo) },
  {
    key: "ultimo_preco", label: "Último preço", largura: 14, texto: textoUltimoPreco,
    render: (p) => <CelulaUltimoPreco ultimo={ultimoPrecoDe(p)} />,
  },
  { key: "custo_medio", label: "Custo médio", largura: 10, alinhar: "right", texto: (p) => (Number(p.custo_medio) > 0 ? formatarReaisBR(p.custo_medio) : "—") },
  {
    key: "valor_estoque", label: "Em estoque", largura: 11, alinhar: "right",
    texto: (p) => formatarReaisBR(valorEstoque(p)),
    render: (p) => <strong style={{ fontWeight: 600 }}>{formatarReaisBR(valorEstoque(p))}</strong>,
  },
  {
    key: "situacao", label: "Situação", largura: 8, texto: (p) => (p.ativo ? "Ativo" : "Inativo"),
    render: (p) => <DotSituacao ativo={p.ativo} />,
  },
  { key: "atualizado", label: "Atualizado", largura: 9, texto: (p) => formatarDataLocal(p.updated_at) },
  {
    key: "alerta", label: "Alerta", largura: 6, texto: (p) => (p.alerta_minimo ? "Abaixo do mínimo" : ""),
    render: (p) => (p.alerta_minimo ? <FiAlertTriangle size={12} style={{ color: "var(--warning-text)" }} title="Abaixo do mínimo" /> : null),
  },
];

export const COLUNAS_PADRAO = ["tipo", "item", "unidade", "saldo", "ultimo_preco", "custo_medio", "valor_estoque", "situacao"];

/** Padrão de antes do redesign: quem nunca personalizou ganha o novo. */
const COLUNAS_PADRAO_ANTIGO = ["codigo", "nome", "categoria", "unidade", "saldo", "custo_medio"];

export const COLUNA_POR_KEY = Object.fromEntries(CATALOGO_COLUNAS.map((c) => [c.key, c]));

/** Mantém só chaves válidas, na ordem dada; vazio (ou o padrão antigo) cai no padrão. */
export function normalizarColunas(keys) {
  const validas = (Array.isArray(keys) ? keys : []).filter((k) => COLUNA_POR_KEY[k]);
  if (!validas.length) return COLUNAS_PADRAO;
  const unicas = [...new Set(validas)];
  if (unicas.join(",") === COLUNAS_PADRAO_ANTIGO.join(",")) return COLUNAS_PADRAO;
  return unicas;
}
